import React, { useId } from 'react';

/**
 * Input — text input with optional leading/trailing adornments and field chrome.
 *
 * Props:
 *   label, hint, error: strings (optional). When provided, renders the field wrapper.
 *   leadingIcon / trailingIcon: ReactNode
 *   size: 'sm' | 'md'
 *   wrapClassName: class for the bordered wrapper
 * All native <input> props pass through (value, onChange, placeholder, type…).
 */
const Input = React.forwardRef(function Input(
  {
    label,
    hint,
    error,
    leadingIcon,
    trailingIcon,
    size = 'md',
    id,
    className = '',
    wrapClassName = '',
    disabled,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id || autoId;
  const invalid = Boolean(error);

  const control = (
    <div
      className={`ps-input-wrap ${size === 'sm' ? 'ps-input-wrap--sm' : ''} ${wrapClassName}`.trim()}
      data-disabled={disabled ? 'true' : undefined}
      data-invalid={invalid ? 'true' : undefined}
    >
      {leadingIcon && <span className="ps-input-wrap__icon">{leadingIcon}</span>}
      <input
        ref={ref}
        id={inputId}
        className={`ps-input ${className}`.trim()}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        {...rest}
      />
      {trailingIcon && <span className="ps-input-wrap__icon">{trailingIcon}</span>}
    </div>
  );

  if (!label && !hint && !error) return control;

  return (
    <div className="ps-field">
      {label && (
        <label className="ps-field__label" htmlFor={inputId}>{label}</label>
      )}
      {control}
      {error ? (
        <span className="ps-field__error" id={`${inputId}-error`}>{error}</span>
      ) : hint ? (
        <span className="ps-field__hint" id={`${inputId}-hint`}>{hint}</span>
      ) : null}
    </div>
  );
});

export default Input;
